import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { Skeleton } from '../ui/skeleton';

const BaseTile = ({
  children,
  title,
  subtitle,
  headerActions = null,
  className = '',
  contentClassName = '',
  padding = 'default', // 'none', 'sm', 'default', 'lg'
  loading = false,
  error = null,
  errorAction = null,
  placeholder = null,
  minHeight = 'default', // 'sm', 'default', 'lg', 'xl', '2xl', 'auto'
  hover = true,
  focus = true,
  ...props
}) => {
  // Shared container classes for all states — h-full fills the grid cell
  const tileClasses = cn(
    'tile-base',
    hover && 'tile-hover',
    focus && 'tile-focus',
    'flex flex-col h-full',
    {
      'p-4': padding === 'sm',
      'p-6': padding === 'default',
      'p-8': padding === 'lg',
      'p-0': padding === 'none',
    },
    {
      'min-h-tile-sm': minHeight === 'sm',
      'min-h-tile': minHeight === 'default',
      'min-h-tile-lg': minHeight === 'lg',
      'min-h-tile-xl': minHeight === 'xl',
      'min-h-tile-2xl': minHeight === '2xl',
    },
    className
  );

  // Handle loading state
  if (loading) {
    return (
      <div className={tileClasses} {...props}>
        <LoadingState title={title} subtitle={subtitle} headerActions={headerActions} />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={tileClasses} {...props}>
        <ErrorState
          title={title}
          subtitle={subtitle}
          error={error}
          errorAction={errorAction}
          headerActions={headerActions}
        />
      </div>
    );
  }

  // Handle placeholder state
  if (placeholder) {
    return (
      <div className={tileClasses} {...props}>
        <PlaceholderState
          title={title}
          subtitle={subtitle}
          placeholder={placeholder}
          headerActions={headerActions}
        />
      </div>
    );
  }

  // Normal state with content
  return (
    <div className={tileClasses} {...props}>
      {/* Header Section */}
      {(title || subtitle || headerActions) && (
        <div className="flex-shrink-0 mb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 text-center pr-2 min-w-0">
              {title && (
                <h3 className="text-xl font-semibold text-gray-800 mb-0 truncate" title={title}>
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-sm text-gray-600 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
            {headerActions && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {headerActions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content Section */}
      <div
        className={cn(
          'flex-grow flex flex-col w-full',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

// Loading State Component — skeleton fills the full cell height
const LoadingState = ({ title, subtitle, headerActions }) => (
  <>
    {/* Header with skeleton */}
    {(title || subtitle || headerActions) && (
      <div className="flex-shrink-0 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 text-center">
            {title && <Skeleton className="h-6 w-3/4 mx-auto mb-2" />}
            {subtitle && <Skeleton className="h-4 w-1/2 mx-auto" />}
          </div>
          {headerActions && (
            <div className="flex items-center gap-1 ml-2">
              {headerActions}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Content skeleton — flex-grow ensures it fills remaining cell height */}
    <div className="flex-grow flex flex-col gap-3 px-2 py-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="flex-1 min-h-[2rem]" />
    </div>
  </>
);

// Error State Component
const ErrorState = ({ title, subtitle, error, errorAction, headerActions }) => {
  const { t } = useTranslation('common');
  return (
  <>
    {/* Header */}
    {(title || subtitle || headerActions) && (
      <div className="flex-shrink-0 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 text-center min-w-0">
            {title && (
              <h3 className="text-xl font-semibold text-gray-800 mb-0 truncate" title={title}>
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">
                {subtitle}
              </p>
            )}
          </div>
          {headerActions && (
            <div className="flex items-center gap-1 ml-2">
              {headerActions}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Error content */}
    <div className="flex-grow status-center">
      <div className="w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
        <svg
          className="w-6 h-6 text-error-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <div className="bg-error-50 border border-error-200 rounded-md px-4 py-3 max-w-sm w-full">
        <p className="text-error-800 text-sm font-medium">
          {typeof error === 'string' ? error : t('status.genericError')}
        </p>
        {error?.message && error.message !== error && (
          <p className="text-error-600 text-xs mt-1">
            {error.message}
          </p>
        )}
      </div>
      {errorAction && (
        <div className="mt-4">
          {errorAction}
        </div>
      )}
    </div>
  </>
  );
};

// Placeholder State Component
const PlaceholderState = ({ title, subtitle, placeholder, headerActions }) => {
  const { t } = useTranslation('common');
  return (
  <>
    {/* Header */}
    {(title || subtitle || headerActions) && (
      <div className="flex-shrink-0 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 text-center min-w-0">
            {title && (
              <h3 className="text-xl font-semibold text-gray-800 mb-0 truncate" title={title}>
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">
                {subtitle}
              </p>
            )}
          </div>
          {headerActions && (
            <div className="flex items-center gap-1 ml-2">
              {headerActions}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Placeholder content */}
    <div className="flex-grow status-center">
      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <p className="text-gray-500 text-sm italic text-center max-w-xs">
        {typeof placeholder === 'string' ? placeholder : t('status.noDataAvailable')}
      </p>
    </div>
  </>
  );
};

export default BaseTile;
